import type { EditorZoomKeyboardContext } from './types/editor-zoom-keyboard-context'
import type { EditorZoomKeyboardEvent } from './types/editor-zoom-keyboard-event'
import type { EditorZoomKeyboardResult } from './types/editor-zoom-keyboard-result'

function isZoomInKey(e: EditorZoomKeyboardEvent): boolean {
  return e.key === '+' || e.key === '=' || e.code === 'Equal' || e.code === 'NumpadAdd'
}

function isZoomOutKey(e: EditorZoomKeyboardEvent): boolean {
  return e.key === '-' || e.code === 'Minus' || e.code === 'NumpadSubtract'
}

/**
 * If this event is a zoom-related shortcut, return how the shell/canvas should react.
 * Returns `null` when the event is not a zoom shortcut (or is blocked by disabled flags).
 */
export function tryResolveEditorZoomKeyboardAction(
  e: EditorZoomKeyboardEvent,
  ctx: EditorZoomKeyboardContext
): EditorZoomKeyboardResult | null {
  const mod = e.ctrlKey || e.metaKey

  if (!mod && !e.altKey && e.shiftKey) {
    if (e.code === 'Digit2' || e.code === 'Numpad2') {
      return { kind: 'fitWidth' }
    }

    if (e.code === 'Digit3' || e.code === 'Numpad3') {
      return { kind: 'zoomToSelectionShift' }
    }
  }

  if (!e.ctrlKey && !e.metaKey && !e.altKey) {
    if (isZoomInKey(e) || isZoomOutKey(e)) {
      if (isZoomInKey(e) && !ctx.canZoomIn) {
        return null
      }

      if (isZoomOutKey(e) && !ctx.canZoomOut) {
        return null
      }

      return isZoomInKey(e) ? { kind: 'zoomIn' } : { kind: 'zoomOut' }
    }
  }

  if (mod && !e.altKey) {
    if (!e.shiftKey && (e.key === '0' || e.code === 'Digit0' || e.code === 'Numpad0')) {
      return { kind: 'actualSize' }
    }

    if (!e.shiftKey && (e.key === '1' || e.code === 'Digit1' || e.code === 'Numpad1')) {
      if (ctx.fitToSpreadDisabled) {
        return null
      }

      return { kind: 'fitToSpread' }
    }

    if (!e.shiftKey && (e.code === 'Digit2' || e.code === 'Numpad2')) {
      return { kind: 'zoomToSelectionCtrl' }
    }

    if (isZoomInKey(e) || isZoomOutKey(e)) {
      if (isZoomInKey(e) && !ctx.canZoomIn) {
        return { kind: 'zoomInBlocked' }
      }

      if (isZoomOutKey(e) && !ctx.canZoomOut) {
        return { kind: 'zoomOutBlocked' }
      }

      return isZoomInKey(e) ? { kind: 'zoomIn' } : { kind: 'zoomOut' }
    }
  }

  return null
}

/**
 * Whether this is a Ctrl/Cmd zoom shortcut (Ctrl/Cmd +, -, 0, 1, 2). These double as the
 * browser's own page-zoom keys and type nothing, so they stay editor zoom even while focus is in
 * a text field — otherwise the browser zooms the page instead (seen in Safari while editing a text frame).
 * Bare +/- and Shift+2/3 are not included: in a text field they type characters.
 */
export function isEditorZoomModifierShortcut(e: EditorZoomKeyboardEvent): boolean {
  if (!e.ctrlKey && !e.metaKey) return false

  return (
    tryResolveEditorZoomKeyboardAction(e, {
      canZoomIn: true,
      canZoomOut: true,
      fitToSpreadDisabled: false,
      zoom: 1,
    }) !== null
  )
}
